/**
 * 이상한 방 - 매치 세션의 효과 번역 (`MatchEffectBody` 하나 → 씬 · HUD · 소리 · 연출)
 *
 * 근거 문서: `이상한방-컨셉시트-v0.1.md` 3절(라운드 구조) · 4.3절(탄환 경제 · 관전) · 4.4절(원래 방 정원 1)
 *            `이상한방-상태모델-v0.1.md` 11.3절(클라이언트가 해도 되는 것) · 12절(동시 클릭)
 *            로드맵 M2(남의 foundChange = 이름표 + 작은 소리 · sniped = "OO이 먼저 찾았다" · 색은 shotResolved를 기다린다)
 *            로드맵 M7(`raised{by}`는 전원에게 - "OO이 걸었다!" 배너 1초 · 내 것이면 칩이 두 겹(뷰) + 선언의 소리)
 *
 * `matchSession.ts`에서 갈라져 나왔다. 싱글의 `session.applyEffect`에 해당하는 자리이고, 원칙도 같다 -
 * **규칙이 하나도 없다.** 탄환을 깎지 않고, 페이즈를 판단하지 않고, 정답을 판정하지 않는다. 호스트가 보낸 효과 하나를
 * 화면의 무엇으로 바꿀지 **고르는 것**만 여기 있다. 재료(자막 키 · 발견 연출 인자 · 소리 이름)는 싱글과 같은
 * `sessionEffects.ts`가 만든다 - 같은 효과 모양은 같은 손으로 옮긴다.
 *
 * ## 판정은 `shotResolved`가 온 뒤에만 색이 된다
 *
 * 탭 순간 세션은 회색 고리 하나(`pending`)만 찍고 `shoot`을 보낸다(로드맵 2.5절). 색·글자·흔들림·자막탑은 전부 여기서,
 * `shotResolved`를 받은 뒤에 찍힌다 - 루프백에서도. 탭 좌표는 `pendingShots`에 도착 순으로 쌓여 있고 호스트는 큐를 도착
 * 순으로 처리하므로(`matchHost.ts` 머리말) 판정도 같은 순서로 돌아온다. 그래서 큐의 머리를 꺼내면 그 탭의 자리다.
 *
 * ## 남의 발견은 정보고 내 발견은 보상이다
 *
 * `foundChange`는 방 전체에 간다(`by`가 있다). 내 것이면 싱글과 같은 자막탑(`juice.hit` + `hitJuiceArgs`)이고, 남의 것이면
 * **그 물건의 자리**에 이름표 한 장(`juice.hit`의 `by`)과 작은 소리(`someoneFound`)다. 남의 탭 좌표는 오지 않으므로
 * (11.2절 - 클라이언트가 가진 것은 두 방과 뷰) 물건의 월드 중심을 화면에 다시 투영한다(`render/scene.projectToClient`).
 * 물건이 두 방 어디에도 없으면(스냅샷이 아직 안 왔다) 이름표는 생략하고 소리만 낸다 - 화면 중앙은 비운다.
 *
 * ## 이 파일에서 갈라져 나간 것
 *
 *  - `matchSessionPhases.ts`  페이즈 진입(`onPhaseEnter` · 자막과 시계 잠금 · 전환의 교체 소유권 · 프록시 · 리빌 · 결과).
 *                             싱글의 `session.onPhaseEnter` 자리다. "수색 진입의 방 교체는 전환 이펙트가 가져간다"의
 *                             전문도 그 머리말로 옮겼다 - 여기 `warpTo` 분기는 그 소유권을 **물어보기만** 한다.
 */

import { SOUND_FOR_GAME_ID, SOUND_FOR_MATCH_ID, type SoundName } from '../audio/sounds'
import { ceilSeconds } from '../core/time'
import type { InstanceId, PlayerId } from '../core/types'
import { t } from '../i18n'
import type { MessageKey } from '../i18n/ko'
import { computeObjectCenterWorld } from '../render/objectView'
import { projectToClient } from '../render/scene'
import type { ShotMarkKind } from '../ui/gameHud'
import { comboPitchStep } from './combo'
import type { MatchEffectBody, MatchPhase } from './match/matchTypes'
import { routeChangerEffect, type ChangerLink } from './matchSessionChanger'
import { routeDecorateEffect, type DecorateLink } from './matchSessionDecorate'
import type { MatchParty } from './matchSessionParty'
import { createMatchPhaseHandler, type MatchPhaseParts } from './matchSessionPhases'
import type { MatchScene } from './matchSessionScene'
import { isMeInOrigin, nameOf } from './matchSessionView'
import { hitJuiceArgs, noticeForEffect, raisedSoundName } from './sessionEffects'

/* --- 타입 ------------------------------------------------------------------------ */

/**
 * 판정을 기다리는 탭 한 발. `kind`는 **기하**가 말한 것(물건을 겨눴는가 · 빈 자리를 겨눴는가)이고 판정이 아니다 -
 * `shotResolved{hit}`가 오면 그 종류의 표식(`object`/`removed`)을 찍는 데만 쓴다.
 */
export type PendingShot = Readonly<{ x: number; y: number; cseq: number; kind: 'object' | 'removed' | null }>

/**
 * 효과 번역이 세션에서 빌려 쓰는 것 전부. 세션 클로저가 아니라 **이 인자만** 본다 - 옮긴 것이 배선임이 드러나게.
 * 페이즈 처리(`MatchPhaseParts`)가 쓰는 것을 포함한다 - 세션은 묶음 하나를 만들고 여기서 그대로 넘긴다.
 */
export type MatchEffectParts = MatchPhaseParts &
  Readonly<{
    me: PlayerId
    /** 판정을 기다리는 탭들(도착 순). 세션이 밀어 넣고 여기서 꺼낸다. */
    pendingShots: PendingShot[]
    startWarp: (toOrigin: boolean, instant: boolean) => void
    onMagazineUsed?: ((remaining: number) => void) | undefined
    onRoundRewards?: ((pins: number) => void) | undefined
    /**
     * 페이즈가 바뀌었다(M4). 무대가 이것으로 바꾸기 진입에 체인저 도구를 세우고 수색 진입에 치운다(`app/stageChanger.ts`).
     * 페이즈 처리(`phases.onPhaseEnter`)가 첫 화면을 세운 **뒤에** 불린다 - 도구가 이어받을 초안이 씬에 이미 있어야 한다.
     */
    onPhaseEnter?: ((phase: MatchPhase, previous: MatchPhase) => void) | undefined
    /** 지금 서 있는 체인저 도구. 없으면 `null` - 체인저 효과는 그때 조용히 지나간다(`matchSessionChanger.ts`). */
    changer: () => ChangerLink | null
    /**
     * 지금 서 있는 꾸미기 도구(M10). 없어도 **씬은 갱신된다** - `placed`는 전원에게 가고 도구를 못 세운 사람의
     * 방만 낡은 채로 두면 그 화면이 거짓말을 한다(`matchSessionDecorate.routeDecorateEffect` 머리말).
     */
    decorator: () => DecorateLink | null
    /** 물건의 자리 조회(`matchSessionScene.objectOf`). 남의 발견 이름표를 그 물건의 자리에 세우는 데 쓴다. */
    objectOf: MatchScene['objectOf']
    /** 방을 한 조각 고친다(`matchSessionScene.decorate`, M10). 씬 전체가 아니라 이 한 손만 받는다 - `objectOf`와 같은 규약. */
    decorate: MatchScene['decorate']
    /**
     * 파티 화면 묶음(M5·M7, `matchSessionParty.ts`). 걸기 배너(`raised{by}`)와 관전 카메라·티커(`foundChange`)는 효과로 뜨므로
     * 여기서 부른다. 나머지(참가자 줄 · 문 앞 줄 · 시점 버튼)는 뷰의 값이라 세션의 `render`가 그린다.
     */
    party: Pick<MatchParty, 'wagerBanner' | 'onFoundChange'>
  }>

export type MatchEffectHandler = Readonly<{
  /** 효과 하나. 한 `step`의 효과는 `applyStep`으로 넘겨라 - 같은 발의 소리 둘을 하나로 고르는 판단이 거기 있다. */
  apply: (effect: MatchEffectBody) => void
  /**
   * 한 `step`의 효과 전부, 순서대로. 하나씩 `apply`하는 것과 한 가지만 다르다 - 같은 `step`에 `sniped`가 있으면 그 발의
   * `shot-blocked` 소리를 건너뛴다. 리듀서는 "이미 찾은 곳"의 소리를 먼저 내고 "남이 방금 찍은 곳"임을 뒤에 덧붙이는데
   * (`matchShoot.onAlreadyFound`), 그 둘은 같은 한 발이라 소리는 하나여야 하고 정보가 더 많은 쪽(`sniped`)이 이긴다.
   * 소리 고르기는 연출이지 규칙이 아니다 - 탄환·콤보·판정은 그대로다.
   */
  applyStep: (effects: readonly MatchEffectBody[]) => void
  /** 결과 화면을 (다시) 띄운다. 배치 모드에서 돌아올 때도 부른다(`matchSessionPhases.showResult`). */
  showResult: () => void
  /** 리빌을 세운다. 뷰에 정답표가 있어야 한다(리빌부터 온다)(`matchSessionPhases.startReveal`). */
  startReveal: () => void
  /** 진행 중인 전환을 접고 그 소유권도 놓는다. 라운드가 접히거나 세션이 잠들 때(`matchSessionPhases.resetTransition`). */
  resetTransition: () => void
}>

/* --- 조각 ------------------------------------------------------------------------ */

/**
 * 소리 요청 → 사전의 이름. 싱글 표(`SOUND_FOR_GAME_ID`)를 먼저, 여럿이 표(`SOUND_FOR_MATCH_ID`)를 그 다음에 본다 -
 * `SoundId | PartySoundId`가 한 효과에 섞여 오므로 `sessionEffects.soundNameFor`(싱글 `SoundId` 전용)를 그대로 못 쓴다.
 * 둘 다 없으면 조용히 넘긴다(표 누락 하나가 라운드를 멈추지 않게 - `sessionEffects` 머리말과 같은 결정).
 * `round-over`는 여기까지 오지 않는다 - `apply`가 `ranking`으로 바꿔 부른다(여럿이의 라운드 끝은 순위 공개다).
 */
function matchSoundName(id: string): SoundName | undefined {
  return SOUND_FOR_GAME_ID[id] ?? SOUND_FOR_MATCH_ID[id]
}

/** `HitTarget`의 종류 → 표식 종류. 기하가 말한 것이지 판정이 아니다(`PendingShot.kind`). */
function markForHit(kind: PendingShot['kind']): ShotMarkKind {
  return kind === 'removed' ? 'removed' : 'object'
}

/* --- 입구 ------------------------------------------------------------------------ */

export function createMatchEffectHandler(parts: MatchEffectParts): MatchEffectHandler {
  const { me, catalog, ctx, hud, juice, audio } = parts

  /** 페이즈 진입과 라운드 수명에 걸친 상태(수색 시작 시각 · 전환의 교체 소유권)는 전부 여기 있다(`matchSessionPhases.ts`). */
  const phases = createMatchPhaseHandler(parts)

  /** 마지막으로 판정이 돌아온 내 탭의 자리. 같은 `step` 안에서 뒤따르는 `foundChange`가 자막탑을 여기 세운다. */
  let lastResolvedShot: Readonly<{ x: number; y: number }> = { x: 0, y: 0 }
  /** 이번 한 발이 콤보를 끊었는가. `comboBroken`이 `shotResolved{miss}`보다 먼저 오므로 그때까지 들고 있다. */
  let comboBrokenThisShot = false

  /* --- 위치 --- */

  /**
   * 물건 하나의 화면 자리. 변경본을 먼저 본다(추가·이동은 거기 있다) - 제거된 것은 원본에만 있다.
   * 카탈로그에 없는 소품은 자리를 못 잡는다(`null`) - 이름표를 화면 중앙에 찍는 것보다 생략이 낫다.
   */
  function clientPointOf(targetId: InstanceId): Readonly<{ x: number; y: number }> | null {
    const obj = parts.objectOf(targetId)
    if (obj === undefined) return null
    const entry = catalog.get(obj.catalogId)
    if (entry === undefined) return null
    return projectToClient(ctx, computeObjectCenterWorld(obj.cell, entry, obj.rot))
  }

  /* --- 자막 · 소리 --- */

  /** 키는 사전의 것이어야 한다(`MessageKey`) - 리터럴 오타를 컴파일이 잡는다. `sessionEffects.Notice.key`와 같은 좁힘. */
  function notice(key: MessageKey, params?: Readonly<Record<string, string | number>>, tone: 'info' | 'good' | 'warn' = 'info'): void {
    hud.showNotice(t(key, params), tone)
  }

  function playSound(id: string): void {
    const name = matchSoundName(id)
    if (name === undefined) return
    // 발견음만 콤보를 읽는다(`sounds.sound`). 뷰의 콤보는 호스트가 방금 올려 둔 값이다.
    audio.play(name, comboPitchStep(parts.view().self.combo))
  }

  /* --- 사격 --- */

  function onShotResolved(effect: Extract<MatchEffectBody, { type: 'shotResolved' }>): void {
    const shot = parts.pendingShots.shift()
    // 짝이 없는 판정(재접속 뒤의 잔여)은 자리를 모른다. 표식 없이 소리만 남는다(소리는 별도 효과다).
    if (shot === undefined) return
    lastResolvedShot = { x: shot.x, y: shot.y }
    const view = parts.view()

    switch (effect.kind) {
      case 'hit':
        // 자막탑이 뒤따르는 `foundChange`에서 같은 자리에 서므로 글자를 지운다 - 겹치면 둘 다 안 읽힌다(`session.shootAt`).
        hud.flashShot(shot.x, shot.y, markForHit(shot.kind), '')
        return
      case 'miss':
        hud.flashShot(shot.x, shot.y, 'miss')
        // 가장자리 플래시는 HUD가 이미 했다(`gh-damage`). 여기서 더하는 것은 흔들림과 끊김 자막이다.
        juice.miss(shot.x, shot.y, { comboBroken: comboBrokenThisShot, flash: false })
        comboBrokenThisShot = false
        return
      case 'already-found':
        hud.flashShot(shot.x, shot.y, 'already-found')
        return
      case 'own-zone':
        /*
         * 내 구획의 이상한 곳(M11 규칙 4). 손해가 0이라 표식은 `already-found`와 같은 회색 고리이고, 이유는 그 자리에
         * 말한다 - 반응 없는 입력은 고장으로 읽힌다(`blocked`와 같은 결정). 문구는 M4의 것을 그대로 쓴다: 그 구획을
         * 다녀간 사람이 나이기 때문이다(새 키를 만들지 않는다).
         */
        hud.flashShot(shot.x, shot.y, 'already-found', t('party.visitorShotBlocked'))
        return
      case 'blocked':
        // 4.4절: 원래 방에서는 쏠 것이 없다. 이유를 그 자리에 띄운다 - 반응 없는 입력은 고장으로 읽힌다.
        // "원래 방 안"은 `inOrigin`(상태)에서 읽는다 - 얼굴(`origin.kind`)에서 거꾸로 읽지 않는다(`matchSessionView.isMeInOrigin`).
        hud.flashShot(shot.x, shot.y, 'dry', isMeInOrigin(view) ? t('hud.originHint') : undefined)
        return
    }
  }

  function onFoundChange(effect: Extract<MatchEffectBody, { type: 'foundChange' }>): void {
    const view = parts.view()
    if (effect.by === me) {
      // 이 게임의 유일한 보상. 자막탑을 **맞은 지점**에 세운다. 넷을 펴는 것은 싱글과 같은 `hitJuiceArgs`다 - 걸기(M7) 여부도
      // 뷰에서 읽는다(호스트가 이 발견 기록에 굳힌 것과 같은 값). 자막의 두 배가 내역의 두 배다.
      juice.hit(lastResolvedShot.x, lastResolvedShot.y, hitJuiceArgs(view.self.combo, view.self.raised))
    } else {
      // 남의 발견. 그 물건의 자리에 이름표 + 작은 소리. 자리를 못 잡으면 이름표만 생략한다(머리말).
      const point = clientPointOf(effect.targetId)
      if (point !== null) juice.hit(point.x, point.y, { comboStep: 0, by: nameOf(view, effect.by) })
      playSound('other-found')
      // 관전 중 따라가는 사람의 것이면 카메라가 그 자리로 가고 티커에 한 줄 쌓인다(M5). 내 것은 걸리지 않는다(`spectateTarget`은 남이다).
      parts.party.onFoundChange(effect, view)
    }
    // "N개 남았다"는 방의 것이라 누가 찾았든 띄운다. 키·톤은 싱글과 같은 손(`noticeForEffect`)이 고른다.
    const remainingNotice = noticeForEffect({
      type: 'foundChange',
      changeId: effect.changeId,
      targetId: effect.targetId,
      remaining: effect.remaining,
    })
    if (remainingNotice !== null) notice(remainingNotice.key, remainingNotice.params, remainingNotice.tone)
  }

  /* --- 원래 방 문 --- */

  function onOriginEntryDenied(effect: Extract<MatchEffectBody, { type: 'originEntryDenied' }>): void {
    switch (effect.reason) {
      case 'cooldown': {
        const denied = noticeForEffect({ type: 'originEntryDenied', remainingMs: effect.remainingMs })
        if (denied !== null) notice(denied.key, denied.params, denied.tone)
        return
      }
      case 'occupied':
        // 줄에 섰다. 몇 번째인지가 이 거절의 유일한 정보다(4.4절 정원 1).
        notice('match.doorOccupied', { position: effect.queuePosition ?? 1 }, 'warn')
        return
    }
  }

  /* --- 분기 --- */

  function apply(effect: MatchEffectBody): void {
    switch (effect.type) {
      case 'phaseEnter':
        phases.onPhaseEnter(effect.phase, effect.previous)
        parts.onPhaseEnter?.(effect.phase, effect.previous)
        return
      case 'roomsChanged':
        // 호스트가 `rooms`를 보낼 신호다. 클라이언트에게는 이미 그 메시지가 먼저 왔다 - 할 일이 없다.
        return
      case 'warpTo':
        // 수색 진입의 교체는 전환이 가져갔다. **한 번만 소비한다**(`matchSessionPhases.ts` 머리말).
        if (phases.consumeTransitionWarp()) return
        // 리빌은 자기 타임라인으로 화면을 뒤집는다. 디졸브를 얹으면 두 연출이 서로를 덮는다.
        parts.startWarp(effect.origin, parts.view().phase === 'reveal')
        return
      case 'syncProxies':
        phases.syncProxies()
        return
      case 'playSound':
        // 여럿이의 라운드 끝은 성공·실패가 아니라 **순위 공개**다. 리듀서는 싱글과 같은 `round-over`를 내지만(패리티), 화면은
        // 그 소리를 순위표의 것(`ranking` - `SOUND_FOR_MATCH_ID`)으로 바꿔 부른다. 연출 선택이지 규칙이 아니다.
        playSound(effect.id === 'round-over' ? 'ranking' : effect.id)
        return
      case 'shotResolved':
        onShotResolved(effect)
        return
      case 'foundChange':
        onFoundChange(effect)
        return
      case 'sniped':
        // 손해 0의 피드백(12절). 조용히 씹으면 버튼 고장으로 읽힌다. 소리는 이 효과의 것이다 - 리듀서는 `PartySoundId`의
        // `sniped`를 스스로 내지 않고(`shot-blocked`를 낸다) 이 피드백을 덧붙일 뿐이라, 화면이 그 자리에서 소리를 고른다.
        notice('match.foundBy', { name: nameOf(parts.view(), effect.by) })
        playSound('sniped')
        return
      case 'comboBroken':
        comboBrokenThisShot = true
        return
      case 'comboUp':
        // 소리와 자막이 뷰의 콤보를 직접 읽는다. 명시적으로 받아 두는 이유는 싱글과 같다(`session.applyEffect`).
        return
      case 'lowAmmo': {
        const low = noticeForEffect({ type: 'lowAmmo', ammo: effect.ammo })
        if (low !== null) notice(low.key, low.params, low.tone)
        return
      }
      case 'magazineUsed':
        // HUD가 `+N`을 스스로 띄운다. 여기서 하는 일은 계정에 알리는 것 하나다(`session.ts`의 같은 효과).
        parts.onMagazineUsed?.(effect.remaining)
        return
      case 'spectating':
        // 내 관전 전환만 말한다. 남의 것은 참가자 줄(`ui/playerStrip.ts`)이 뷰의 값으로 표식을 붙인다 - 효과 배선이 없다.
        if (effect.playerId === me && effect.target === null && parts.view().self.spectating) {
          notice('match.spectating', undefined, 'warn')
        }
        return
      case 'originEntryDenied':
        onOriginEntryDenied(effect)
        return
      case 'originReady':
        // 쿨타임이 끝났다. 할 일은 없다 - 이 효과가 실린 `step`의 뷰가 이미 `ready` 얼굴이고 HUD가 그것을 그린다. 효과의
        // 존재 이유는 그 `step`이 오게 하는 것 자체다(`matchTypes` 주석). 소리·자막을 얹지 않는다 - 5초마다 울리는 알림이 된다.
        return
      case 'doorYourTurn':
        notice('match.doorYourTurn', { seconds: ceilSeconds(effect.graceMs) }, 'good')
        return
      case 'roundRewards':
        parts.onRoundRewards?.(effect.pins)
        return
      case 'changeAccepted':
      case 'changeRejected':
        // 내 요청의 답(M4). 소리 · 토스트 · 도구의 대기 줄. 도구가 없으면(수색 중이다) 아무것도 없다.
        routeChangerEffect(effect, parts.changer(), audio, hud, parts.view().myZone !== null)
        return
      case 'changerProgress':
        /*
         * 진행 점(M11). 전원이 동시에 만들므로 **내 것만** 도구에 전한다 - 옆 사람의 수용이 내 대기 요청을 확정하면
         * 초안이 호스트와 어긋난다. 남의 진행은 기다리는 화면이 뷰(`PublicPlayer.changeUsed`)에서 프레임마다 옮긴다.
         */
        if (effect.by === me) routeChangerEffect(effect, parts.changer(), audio, hud, parts.view().myZone !== null)
        return
      case 'raised': {
        /*
         * 걸기(M7). 전원에게 배너 한 줄 - 누가 판을 키웠는지가 방 전체에 보여야 말싸움이 된다(컨셉시트 5절). 내 것이면
         * 싱글과 같은 토스트(`noticeForEffect`)와 선언의 소리를 얹는다. 칩이 두 겹이 되는 것은 뷰(`self.raised`)가 말한다.
         */
        const view = parts.view()
        // 내 것이면 문장이 갈린다('나가 걸었다!'는 이름 자리에 '나'를 넣은 문장이다 - 리빌 이름표의 'mine' 얼굴과 같은 결정).
        parts.party.wagerBanner.show(nameOf(view, effect.by), effect.by === me)
        if (effect.by === me) {
          const raisedNotice = noticeForEffect({ type: 'raised' })
          if (raisedNotice !== null) notice(raisedNotice.key, raisedNotice.params, raisedNotice.tone)
          audio.play(raisedSoundName())
        }
        return
      }
      case 'doorChanged':
        // 문 앞 줄(`ui/doorQueue.ts`)은 이 효과가 아니라 뷰(`door`)로 그린다 - 같은 `step`의 뷰에 같은 값이 실려 있고, 보유자의 남은
        // 체류는 시계 보간이 이어야 하므로 효과 한 장의 스냅샷보다 뷰가 원본이다(M5 규칙 5). 문이 열리는 소리도 뷰의 전이에서 난다.
        return
      /*
       * 꾸미기(M10). 방이 **조각 단위로** 자란다 - 배치 하나마다 방 전체를 다시 싣지 않는다(8명 × 8개 × 1분).
       * 씬 갱신은 도구가 없어도 돌고 소리·토스트만 내 것으로 갈린다(`matchSessionDecorate.routeDecorateEffect`).
       */
      case 'placed':
      case 'unplaced':
      case 'placeRejected':
        routeDecorateEffect(effect, parts.decorator(), { decorate: parts.decorate, audio, hud, me })
        return
      /* 아래는 이 몫이 그리지 않는다 - 관전자 참여(M8). */
      case 'revealStep':
      case 'predictionScored':
      case 'roundScored':
        return
    }
  }

  function applyStep(effects: readonly MatchEffectBody[]): void {
    const sniped = effects.some((effect) => effect.type === 'sniped')
    for (const effect of effects) {
      // 같은 발의 두 소리 중 하나만. 판정 표식(`shotResolved{already-found}`)과 자막은 그대로 간다.
      if (sniped && effect.type === 'playSound' && effect.id === 'shot-blocked') continue
      apply(effect)
    }
  }

  return {
    apply,
    applyStep,
    showResult: phases.showResult,
    startReveal: phases.startReveal,
    resetTransition: phases.resetTransition,
  }
}
